import { action, makeObservable, observable, runInAction } from "mobx";
import { ApplicationSdk } from "..";
import { SEARCH_DELAY } from "../../constant";
import { formatName } from "../../helper";

/**
 * Load all objects and make it possible to manage them
 */
class ObjectManagerStore {
    private sdk: ApplicationSdk;

    public objectListLoading: boolean = false;
    public objectList: Array<any> = [];
    public sortOrder: number = 1;
    public sortField: string = 'object_number';
    public filterType: string | null = null;
    public searchValue: string = '';
    public includeArchived: boolean = false;
    public available_managers: Array<{ id: number; name: string }> = []; // List of available managers
    public filterManager: any = null;

    public currentPage: number = 1;
    public maxPage: number = 1;
    public totalCount: number = 0;
    public latestPage: number = 1;

    public timeout: any = null;

    constructor(sdk: ApplicationSdk) {
        makeObservable(this, {
            filterManager: observable,
            available_managers: observable,
            includeArchived: observable,
            objectListLoading: observable,
            objectList: observable,
            sortOrder: observable,
            sortField: observable,
            filterType: observable,
            searchValue: observable,
            currentPage: observable,
            maxPage: observable,
            totalCount: observable,
            onSort: action,
            updateSearch: action,
            clearSearch: action,
            onPageChange: action,
            setIncludeArchived: action,
            fetchObjectList: action,
            applyFilterManager: action
        });

        this.sdk = sdk;

        // Load state from localStorage
        this.loadFromLocalStorage();
    }

    private saveToLocalStorage(): void {
        const state = {
            sortOrder: this.sortOrder,
            sortField: this.sortField,
            searchValue: this.searchValue,
            archived: this.includeArchived,
            filterManager: this.filterManager
        };
        localStorage.setItem('object', JSON.stringify(state));
    }

    private loadFromLocalStorage(): void {
        const savedState = localStorage.getItem('object');
        if (savedState) {
            try {
                const { sortOrder, sortField, searchValue, archived, filterManager } = JSON.parse(savedState);
                runInAction(() => {
                    this.sortOrder = sortOrder || 1;
                    this.sortField = sortField || 'object_number';
                    this.searchValue = searchValue || '';
                    this.includeArchived = archived || false;
                    this.filterManager = filterManager || null; // Reset filterManager to null on load
                });
            } catch (error) {
                console.error('Failed to load state from localStorage:', error);
            }
        }
    }

    public applyFilterManager(manager: any): void {
        this.filterManager = manager;
        this.saveToLocalStorage();
        this.fetchObjectList();
    }

    public onPageChange(nextPage: number): void {
        this.fetchObjectList(nextPage);
    }

    public setIncludeArchived(value: boolean): void {
        this.includeArchived = value;
        this.saveToLocalStorage();
        this.fetchObjectList();
    }

    public async init(): Promise<void> {
        this.fetchObjectList();
    }

    public async onDialogLeave(dialog: string, event: any = null): Promise<void> {
        if (dialog === 'object' && event?.event === 'SAVED') {
            await this.fetchObjectList(this.latestPage);
        }
    }

    public async onSearch(): Promise<void> {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
        await this.fetchObjectList();
    }

    public async onSort(sortField: string): Promise<void> {
        runInAction(() => {
            if (this.sortField === sortField) {
                this.sortOrder = this.sortOrder === 1 ? -1 : 1;
            } else {
                this.sortOrder = -1;
            }
            this.sortField = sortField;

            // Save updated state to localStorage
            this.saveToLocalStorage();
        });

        await this.fetchObjectList();
    }

    public async fetchObjectList(page: number = 1): Promise<void> {
        this.latestPage = page;
        
        runInAction(() => {
            this.objectListLoading = true;
        });
        const orderListResult: any = await this.sdk.fetchApi(
            'object',
            'query',
            {
                sortvalue: this.sortField,
                archived: this.includeArchived,
                sort: this.sortOrder === 1 ? 'asc' : 'desc',
                search: this.searchValue || '',
                page,
                ...(this.filterManager ? { filter: { type: 'manager', value: this.filterManager.id } } : {})
            });

            runInAction(() => {
            this.objectListLoading = false;
            
            if (orderListResult?.status === 200) {
                this.objectList = (orderListResult.json.data || []).map((item: any) => ({
                    ...item,
                    manager: item.manager ? {
                        ...item.manager,
                        name: formatName(item.manager.firstname, item.manager.lastname)
                    } : item.manager,
                }));
                this.currentPage = orderListResult.json.pagination.page;
                this.totalCount = orderListResult.json.pagination.total;
                this.maxPage = Math.ceil(this.totalCount / orderListResult.json.pagination.pageSize);
            }
        });
    }

    public async deleteObject(object_id: number): Promise<boolean> {
        if (object_id) {
            const objData = {
                object_id
            };
            const responseQuestion = await this.sdk.fetchApi(
                'object',
                'delete',
                objData
            );

            if (responseQuestion.status === 200) {
                this.fetchObjectList(this.latestPage);
                return true;
            } else {
                const errorCode = responseQuestion?.json?.error || '';

                if (errorCode) {
                    switch (errorCode) {
                        case 'ERR_OBJECT_NOT_ARCHIVED':
                            throw new Error('Das Objekt kann nur gelöscht werden, wenn es archiviert ist.');
                        case 'ERR_OBJECT_NOT_FOUND':
                            throw new Error('Das Objekt wurde nicht gefunden.');
                        default:
                            break;
                    }
                }

                throw new Error('Ein unbekannter Fehler ist aufgetreten');
            }
        }
        return false;
    }

    public async fetchAvailableManagers(force: boolean = false): Promise<void> {
        if (!force) {
            if (this.available_managers.length > 1) {
                return;
            }
        }
        const managerResponse: any = await this.sdk.fetchApi('manager', 'query', {});

        runInAction(() => {
            if (managerResponse?.status === 200) {
                this.available_managers = managerResponse.json.data.map((manager: any) => ({
                    id: manager.id,
                    name: formatName(manager.firstname, manager.lastname)
                }));
            }
        });
    }

    public clearSearch(): void {
        if (this.searchValue) {
            this.searchValue = '';
            this.saveToLocalStorage();
            this.fetchObjectList();
        }
    }

    public updateSearch(value: string): void {
        this.searchValue = value;

        // Save updated state to localStorage
        this.saveToLocalStorage();

        if (this.timeout) {
            clearTimeout(this.timeout);
        }

        if (SEARCH_DELAY) {
            this.timeout = setTimeout(() => {
                this.fetchObjectList();
            }, SEARCH_DELAY);
        }
    }

    get allObjects(): Array<any> | null {
        if (!this.objectList) return null;
        return this.objectList.filter((item: any) => {
            return true;
        }).map((item: any) => ({
            ...item,
            address: `${item.address.street} ${item.address.housenumber}, ${item.address.zipcode} ${item.address.city}`
        }));
    }

    public onLeaveStore(): void {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
    }

}

export default ObjectManagerStore;